export type ZonedWallTimeResult =
  | { ok: true; instant: Date }
  | { ok: false; reason: 'invalid' | 'nonexistent' | 'ambiguous' };

const WALL_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

function partsAt(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}:${value('second')}`;
}

export function resolveZonedWallTime(
  wallTime: string,
  timeZone: string,
): ZonedWallTimeResult {
  const match = WALL_TIME_PATTERN.exec(wallTime);
  if (!match) return { ok: false, reason: 'invalid' };
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format();
  } catch {
    return { ok: false, reason: 'invalid' };
  }

  const [, year, month, day, hour, minute, second = '00'] = match;
  const normalized = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  const assumedUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
  const matches: Date[] = [];
  for (let offsetMinutes = -14 * 60; offsetMinutes <= 14 * 60; offsetMinutes += 15) {
    const candidate = new Date(assumedUtc - offsetMinutes * 60_000);
    if (partsAt(candidate, timeZone) === normalized) matches.push(candidate);
  }
  const unique = [...new Map(matches.map((value) => [value.getTime(), value])).values()];
  if (unique.length === 0) return { ok: false, reason: 'nonexistent' };
  if (unique.length > 1) return { ok: false, reason: 'ambiguous' };
  return { ok: true, instant: unique[0]! };
}

export function formatInstantAsWallTime(instant: Date | string, timeZone: string): string {
  return partsAt(new Date(instant), timeZone).slice(0, 16);
}
